import type { CommentTextSelectionUpdate } from '@getaccept/editor-lib';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';
import type { Comment, Reply } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import type { CommentReadEvent } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-read-event';
import type { UpdateCommentDto } from '@getaccept/lib-shared/src/contextual-commenting/types/update-comment-dto';
import type { ComputedRef, Ref } from '@vue/composition-api';
import { computed, ref } from '@vue/composition-api';
import { defineStore } from 'pinia';

export const useContextualCommentingStore = defineStore('contextual-commenting', () => {
  const comments: Ref<Comment[]> = ref([]);

  const viewableComments: ComputedRef<Comment[]> = computed(() =>
    comments.value.filter((comment: Comment) =>
      CommentsHelper.isViewableComment(comment, currentUserId.value)
    )
  );

  const setComments = (newComments: Comment[]) => {
    comments.value = newComments;
  };

  const currentUserId = ref('');
  const setCurrentUserId = (userId: string) => {
    currentUserId.value = userId;
  };

  const updateComment = (payload: Partial<UpdateCommentDto>) => {
    comments.value = comments.value.map((existingComment: Comment) =>
      existingComment.id === payload.commentId
        ? { ...existingComment, ...payload }
        : existingComment
    );
  };

  const replaceComment = (comment: Comment) => {
    comments.value = comments.value.map((existingComment: Comment) =>
      existingComment.id === comment.id ? comment : existingComment
    );
  };

  const addComment = (comment: Comment) => {
    comments.value = [...comments.value, comment];
  };

  const removeComment = (commentId: string) => {
    comments.value = comments.value.filter((comment: Comment) => comment.id !== commentId);
  };

  const selectedCommentId = ref('');

  const selectComment = (commentId: string) => (selectedCommentId.value = commentId);

  const selectedComment = computed(() =>
    comments.value.find(comment => comment.id === selectedCommentId.value)
  );

  const hoveredCommentId = ref('');

  const hoverComment = (commentId: string) => (hoveredCommentId.value = commentId);

  const hoveredComment = computed(() =>
    comments.value.find(comment => comment.id === hoveredCommentId.value)
  );

  const addReply = (commentId: string, reply: Reply) => {
    comments.value = CommentsHelper.addReply(reply, commentId, comments.value);
  };

  const addReadEvent = (commentId: string, readEvent: CommentReadEvent) => {
    comments.value = CommentsHelper.addRead(readEvent, commentId, comments.value);
  };

  const addReplyReadEvent = (commentId: string, replyId: string, readEvent: CommentReadEvent) => {
    comments.value = CommentsHelper.addReplyRead(readEvent, replyId, commentId, comments.value);
  };

  const reset = () => {
    comments.value = [];
    selectedCommentId.value = '';
    currentUserId.value = '';
  };

  const updatedTextSelections: Ref<CommentTextSelectionUpdate[]> = ref([]);

  const updateTextSelections = (payload: CommentTextSelectionUpdate[]) => {
    const unchangedCommentHighlights = updatedTextSelections.value.filter(
      updated => !payload.find(newUpdate => newUpdate.id === updated.id)
    );
    updatedTextSelections.value = [...unchangedCommentHighlights, ...payload];
  };

  return {
    setComments,

    setCurrentUserId,

    updateComment,
    addComment,
    removeComment,
    replaceComment,

    selectComment,
    selectedCommentId,
    selectedComment,

    hoverComment,
    hoveredCommentId,
    hoveredComment,

    addReply,

    addReadEvent,
    addReplyReadEvent,

    reset,

    comments,
    viewableComments,

    updateTextSelections,
    updatedTextSelections,
  };
});
